/**
 * 支払キュー(2026-10-23・Gate 支出側 段階2・/queue)。経理・承認者・管理者向け。
 * 経費申請・取引先の請求書・繰り返し支払い・外部システムの確定のうち、まだ支払っていないものを期日順に並べる。
 * **見るだけの一覧**: 承認・支払いの操作は今までの画面(承認・支払い待ち)で行う。並べ方と期日の決め方は payQueue.ts。
 * PC は表、スマホ(768px以下)は1件1行のリスト(横スクロールなし)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import { formatYen } from '@core/lib/money';
import { todayStr, toShortDate } from '@core/lib/date';
import { useIsMobile } from '@core/lib/useIsMobile';
import { STATUS_LABELS, type Payer, type Vendor } from '@core/lib/types';
import { fetchVendors } from '@core/masters/vendorRepo';
import {
  BUCKET_LABELS,
  ORIGIN_LABELS,
  buildPayQueue,
  summarizeQueue,
  type DueBucket,
  type QueueOrigin,
  type QueueRow,
} from './payQueue';
import { fetchPayQueueTransactions } from './payQueueRepo';
import { externalOriginLabel } from '@core/routine/ExternalDetail';

type BucketFilter = 'all' | DueBucket | 'before';

const BUCKETS: DueBucket[] = ['overdue', 'today', 'week', 'month', 'later'];

/** 操作する画面。承認前は承認(承認の画面に入れるのは部門長・承認者だけ)、承認済み以降は支払い待ち */
/** 発生元の表示。外部システムは名前まで出す(例: Routine(運送)の確定。2026-10-23) */
function originText(r: QueueRow): string {
  return r.origin === 'external' ? (externalOriginLabel(r.transaction.external) ?? ORIGIN_LABELS.external) : ORIGIN_LABELS[r.origin];
}

function actionPathOf(r: QueueRow, canOpenApprove: boolean): { to: string; label: string } | null {
  if (r.beforeApproval) return canOpenApprove ? { to: '/approve', label: '承認へ' } : null;
  return { to: '/pay', label: '支払い待ちへ' };
}

export default function PayQueuePage() {
  const { departmentName, accountName, payerLabels } = useMasters();
  const { flowRole } = useAuth();
  const canOpenApprove = flowRole === 'bucho' || flowRole === 'approver';
  const toast = useToast();
  const mobile = useIsMobile();
  const [rows, setRows] = useState<QueueRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState<BucketFilter>('all');
  const [origin, setOrigin] = useState<'all' | QueueOrigin>('all');
  const [payer, setPayer] = useState<'all' | Exclude<Payer, null>>('all');
  const today = todayStr();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ rows: transactions, truncated: cut }, vendors] = await Promise.all([
        fetchPayQueueTransactions(),
        fetchVendors(true).catch(() => [] as Vendor[]),
      ]);
      setRows(buildPayQueue(transactions, vendors, today));
      setTruncated(cut);
    } catch (e) {
      toast(e instanceof Error ? e.message : '支払キューを読み込めませんでした');
    } finally {
      setLoading(false);
    }
  }, [toast, today]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = useMemo(() => summarizeQueue(rows), [rows]);
  const shown = useMemo(
    () =>
      rows.filter(
        (r) =>
          (bucket === 'all' ||
            (bucket === 'before' ? r.beforeApproval : !r.beforeApproval && r.bucket === bucket)) &&
          (origin === 'all' || r.origin === origin) &&
          (payer === 'all' || r.transaction.payer === payer),
      ),
    [rows, bucket, origin, payer],
  );
  const shownTotal = shown.reduce((s, r) => s + r.transaction.amount, 0);

  const payerText = (p: Payer) => (p ? payerLabels[p] : '—');
  const payeeOf = (r: QueueRow) => r.transaction.counterparty ?? '—';

  return (
    <section className="screen queue-screen">
      <div className="card">
        <h3>支払キュー(まだ支払っていないもの)</h3>
        <p className="hint" style={{ margin: '0 0 10px' }}>
          経費申請・取引先の請求書・繰り返し支払い・外部システムの確定を、期日の早い順に並べています。承認・支払いの操作は各画面で行います。
        </p>

        <div className="queue-summary">
          {BUCKETS.map((b) => (
            <button
              key={b}
              className={`queue-tile${bucket === b ? ' on' : ''}${b === 'overdue' && summary.overdue.count > 0 ? ' alert' : ''}`}
              onClick={() => setBucket(bucket === b ? 'all' : b)}
            >
              <span className="queue-tile-label">{BUCKET_LABELS[b]}</span>
              <span className="queue-tile-count">{summary[b].count}件</span>
              <span className="queue-tile-amount">¥{formatYen(summary[b].amount)}</span>
            </button>
          ))}
          <button
            className={`queue-tile before${bucket === 'before' ? ' on' : ''}`}
            onClick={() => setBucket(bucket === 'before' ? 'all' : 'before')}
          >
            <span className="queue-tile-label">承認前</span>
            <span className="queue-tile-count">{summary.beforeApproval.count}件</span>
            <span className="queue-tile-amount">¥{formatYen(summary.beforeApproval.amount)}</span>
          </button>
        </div>

        <div className="queue-filters">
          <select value={origin} onChange={(e) => setOrigin(e.target.value as 'all' | QueueOrigin)}>
            <option value="all">発生元: すべて</option>
            {(Object.keys(ORIGIN_LABELS) as QueueOrigin[]).map((o) => (
              <option key={o} value={o}>
                {ORIGIN_LABELS[o]}
              </option>
            ))}
          </select>
          <select value={payer} onChange={(e) => setPayer(e.target.value as 'all' | Exclude<Payer, null>)}>
            <option value="all">支払い者: すべて</option>
            {(['accounting', 'bucho', 'self'] as const).map((p) => (
              <option key={p} value={p}>
                {payerLabels[p]}
              </option>
            ))}
          </select>
          <span className="hint">
            {shown.length}件・¥{formatYen(shownTotal)}
          </span>
        </div>

        {truncated && (
          <p className="hint" style={{ color: 'var(--red)', margin: '0 0 8px' }}>
            件数が多いため一部だけ表示しています。
          </p>
        )}

        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : shown.length === 0 ? (
          <p className="empty">該当するものはありません</p>
        ) : mobile ? (
          <div className="queue-list">
            {shown.map((r) => {
              const t = r.transaction;
              const action = actionPathOf(r, canOpenApprove);
              const body = (
                <>
                  <div className="queue-item-main">
                    <span className={r.bucket === 'overdue' && !r.beforeApproval ? 'neg' : undefined}>
                      {toShortDate(r.dueDate)}
                    </span>
                    <span className="num">¥{formatYen(t.amount)}</span>
                  </div>
                  <div className="queue-item-sub">
                    <span>{STATUS_LABELS[t.status]}</span>
                    <span>{originText(r)}</span>
                    <span>{departmentName(t.department_id)}</span>
                    <span>{payeeOf(r)}</span>
                    <span>{accountName(t.account_id)}</span>
                  </div>
                </>
              );
              return action ? (
                <Link key={t.id} to={action.to} className="queue-item">
                  {body}
                </Link>
              ) : (
                <div key={t.id} className="queue-item">
                  {body}
                </div>
              );
            })}
          </div>
        ) : (
          <table className="no-stack queue-table">
            <thead>
              <tr>
                <th>期日</th>
                <th>状態</th>
                <th>発生元</th>
                <th>事業部</th>
                <th>支払先</th>
                <th>科目</th>
                <th className="num">金額</th>
                <th>支払い者</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const t = r.transaction;
                const action = actionPathOf(r, canOpenApprove);
                return (
                  <tr key={t.id}>
                    <td
                      className={r.bucket === 'overdue' && !r.beforeApproval ? 'neg' : undefined}
                      title={r.dueFrom === 'terms' ? '取引先の支払条件から計算' : undefined}
                    >
                      {toShortDate(r.dueDate)}
                    </td>
                    <td>{STATUS_LABELS[t.status]}</td>
                    <td>{originText(r)}</td>
                    <td>{departmentName(t.department_id)}</td>
                    <td className="queue-payee">{payeeOf(r)}</td>
                    <td>{accountName(t.account_id)}</td>
                    <td className="num">{formatYen(t.amount)}</td>
                    <td>{payerText(t.payer)}</td>
                    <td>
                      {action ? (
                        <Link to={action.to} className="txn-link">
                          {action.label}
                        </Link>
                      ) : (
                        <span className="hint">承認者の承認待ち</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
